/**
 * Created by andrey on 23.07.2025
 */

/**
 * connector.conversions provides methods for tracking various conversions events such as events, purchases,
 * ad revenue, and updating user information. It acts as an interface for managing and
 * processing analytics data through a connected analytics service.
 *
 * ### **Supported Conversions Platforms**
 * The conversions interface currently supports event tracking for the following platforms:
 *
 * #### Game platforms
 * Events are reported to the host platform's native SDK (store dashboards, platform-side stats, etc.).
 * <ul class="platforms-list">
 *   <li>
 *       ✅ <img src="images/instant.png"> Facebook Instant Games
 *   </li>
 *   <li>
 *       ✅ <img src="images/samsung.png"> Samsung
 *   </li>
 *   <li>
 *       ✅ <img src="images/yandex.png"> Yandex Games
 *   </li>
 * </ul>
 *
 * #### Analytics &amp; attribution
 * Third-party trackers that work across platforms, usually configured via app tokens / counter IDs:
 * - **Adjust**
 * - **Facebook Pixel**
 * - **Firebase**
 * - **Google Analytics**
 * - **Yandex Metrika**
 *
 * #### **Adjust Configuration**
 * Adjust is integrated into the conversions framework using a configuration file (e.g., ).
 * This file defines critical parameters such as app tokens, and event mappings:
 * ``` json
 *     "token": "6dh55t109vr4",
 *     "purchaseToken": "6gffsu",
 *     "events": {
 *         "levelup": "r5df4a",
 *         "visit": "gftdsa",
 *  ```
 *
 * #### **Yandex Metrika Configuration**
 * Yandex Metrika is also integrated into the framework using configuration details typically provided as part of a larger file.
 * It includes the Yandex Metrika counter ID alongside other settings:
 * ``` json
 *    "yandex": {
 *         "appId": "453265",
 *         "counter": 365854621
 *     },
 *  ```
 *
 * Example level up event usage:
 * ```javascript
 * connector.conversions.trackEvent(connector.EVENTS.CONVERSIONS.LEVEL_UP);
 *
 * connector.conversions.trackEvent(connector.EVENTS.CONVERSIONS.EARN_SOFT, 100);
 * ```
 * @namespace Conversions
 */
const ConversionsInterface = function () {
    return {

        /**
         * @memberof Conversions
         * @desc Tracks an event with the specified name and parameters.
         * @param {string} name - The name of the event to be logged.
         * @param {Object|number} params - An object containing key-value pairs of parameters associated with the event
         * or a single numeric value
         */
        trackEvent: function (name, params = {}) {
            connector._conversions.trackEvent(name, params);
        },

        /**
         * @function trackPurchase
         * @memberof Conversions
         * @desc Tracks a purchase event with the provided product, purchase, and additional parameters.
         * @param {Object} product - The product information related to the purchase.
         * @param {Object} purchase - The details of the purchase being logged.
         * @param {Object} [params] - Additional parameters for logging the purchase (optional).
         */
        trackPurchase: function (product, purchase, params) {
            connector._conversions.trackPurchase(product, purchase, params);
        },

        /**
         * @function trackAdRevenue
         * @memberof Conversions
         * @desc Tracks advertisement revenue data.
         * @param {string} name - The name or identifier of the advertisement.
         * @param {number} impressionCost - The cost per impression of the advertisement.
         * @param {Object} params - Additional parameters or metadata associated with the advertisement.
         */
        trackAdRevenue: function (name, impressionCost, params) {
            connector._conversions.trackAdRevenue(name, impressionCost, params);
        },

        /**
         * @memberof Conversions
         * @desc Sends user information, including the updated user ID, to Firebase for analytics tracking.
         */
        trackUserId: function () {
            connector._conversions.trackUserId();
        },

        /**
         * @memberof Conversions
         * @desc Stops reporting to the attribution and analytics loggers for the rest of the install, the metrics logger keeps working;
         * the game calls it for an install it has proved to be an ad farm, so its ad revenue does not teach the networks to buy more of it
         */
        disable: function () {
            connector._conversions.disable();
        }
    };
};